import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getPublicRegionalPricing } from '../services/api';
import {
  applyRegionalPrice,
  formatRegionalPrice,
  getDefaultRegionOptions,
  getRegionByCurrency,
  getRegionConfig,
  getStoredPricingRegion,
  mergeApiRegions,
  normalizeRegionId,
  regionalizeModuleGroups,
  storePricingRegion,
} from '../data/regionalPricing';

const PricingRegionContext = createContext();

export const PricingRegionProvider = ({ children }) => {
  const savedRegion = getStoredPricingRegion();
  const initialRegion = savedRegion || 'US_GLOBAL';
  const [selectedRegionId, setSelectedRegionId] = useState(initialRegion);
  const [regions, setRegions] = useState(getDefaultRegionOptions());
  const [prices, setPrices] = useState([]);
  const [hasManualOverride, setHasManualOverride] = useState(Boolean(savedRegion));
  const [isPricingReady, setIsPricingReady] = useState(false);

  const trackPricingEvent = useCallback((eventName, payload = {}) => {
    const detail = {
      event: eventName,
      pricingRegion: selectedRegionId,
      currencyCode: getRegionConfig(selectedRegionId, regions).currencyCode,
      ...payload,
    };

    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('webouse_pricing_event', { detail }));
  }, [regions, selectedRegionId]);

  const loadRegionalPricing = useCallback(async (regionForQuery = null) => {
    setIsPricingReady(false);
    try {
      const response = await getPublicRegionalPricing(regionForQuery);
      const fallbackRegion = regionForQuery || 'US_GLOBAL';
      setRegions(mergeApiRegions(response.regions || []));
      setPrices(response.prices || []);
      setSelectedRegionId(
        normalizeRegionId(response.selected_region || fallbackRegion)
      );
    } catch (error) {
      console.warn('Regional pricing fallback is active:', error);
      setPrices([]);
      setSelectedRegionId(normalizeRegionId(regionForQuery || 'US_GLOBAL'));
    } finally {
      setIsPricingReady(true);
    }
  }, []);

  useEffect(() => {
    loadRegionalPricing(hasManualOverride ? selectedRegionId : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePricingRegion = useCallback((regionId) => {
    const normalized = normalizeRegionId(regionId);
    setHasManualOverride(true);
    setSelectedRegionId(normalized);
    storePricingRegion(normalized);
    loadRegionalPricing(normalized);
    trackPricingEvent('pricing_region_changed', {
      selectedRegion: normalized,
      currencyCode: getRegionConfig(normalized, regions).currencyCode,
    });
  }, [loadRegionalPricing, regions, trackPricingEvent]);

  const changePricingCurrency = useCallback((currencyCode) => {
    changePricingRegion(getRegionByCurrency(currencyCode));
  }, [changePricingRegion]);

  const pricingRegion = useMemo(
    () => getRegionConfig(selectedRegionId, regions),
    [regions, selectedRegionId]
  );

  const formatPrice = useCallback(
    (value) => formatRegionalPrice(value, selectedRegionId, regions),
    [regions, selectedRegionId]
  );

  const localizeItemPrice = useCallback(
    (item, itemType) =>
      applyRegionalPrice(item, itemType, selectedRegionId, prices, regions),
    [prices, regions, selectedRegionId]
  );

  const localizeModuleGroups = useCallback(
    (groups) => regionalizeModuleGroups(groups, selectedRegionId, prices, regions),
    [prices, regions, selectedRegionId]
  );

  const value = useMemo(() => ({
    pricingRegion,
    pricingRegionId: selectedRegionId,
    currencyCode: pricingRegion.currencyCode,
    regions,
    prices,
    hasManualOverride,
    isPricingReady,
    changePricingRegion,
    changePricingCurrency,
    formatPrice,
    localizeItemPrice,
    localizeModuleGroups,
    trackPricingEvent,
  }), [
    changePricingCurrency,
    changePricingRegion,
    formatPrice,
    hasManualOverride,
    isPricingReady,
    localizeItemPrice,
    localizeModuleGroups,
    prices,
    pricingRegion,
    regions,
    selectedRegionId,
    trackPricingEvent,
  ]);

  return (
    <PricingRegionContext.Provider value={value}>
      {children}
    </PricingRegionContext.Provider>
  );
};

export const usePricingRegion = () => {
  const context = useContext(PricingRegionContext);
  if (!context) {
    throw new Error('usePricingRegion must be used within PricingRegionProvider');
  }
  return context;
};
